import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { MdOutlineSearch, MdOutlinePieChart, MdOutlineAddCircleOutline } from 'react-icons/md';
import { Utils, fetchWithAuth } from '../../utils';

/**
 * Shared "Select Segment" picker - used by Automation Settings (Entry Rules Include/Exclude
 * Segment, Exit Rules "Enters a segment") AND the Condition node's "Segment membership"
 * branch, so there is exactly one segment-picker UI in the Automation Builder, not two.
 *
 * Pass `segments` when the caller already has the list fetched (Automation Settings keeps its
 * own `segments` state for other uses too, like the Recommended Channels push count) to avoid
 * fetching twice. Omit it (just pass `appId`) and this component fetches it itself on open.
 */
const SegmentPickerModal = ({ show, onHide, appId, segments, excludeIds = [], onSelect, manageLink }) => {
    const [fetchedSegments, setFetchedSegments] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const list = segments || fetchedSegments;

    useEffect(() => {
        if (!show || segments || !appId) return;
        setFetching(true);
        fetchWithAuth(Utils.baseurl + 'get/segments', {
            method: 'POST',
            body: JSON.stringify({ app_id: appId, pageSize: 1000 }),
        })
            .then((r) => r.json())
            .then((result) => { if (result.status) setFetchedSegments(result.data || []); })
            .catch(() => {})
            .finally(() => setFetching(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, appId]);

    const filteredList = list
        .filter((s) => !excludeIds.includes(s.id))
        .filter((s) => s.name?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <Modal show={show} onHide={() => { onHide(); setSearchTerm(''); }} centered>
            <Modal.Header closeButton>
                <Modal.Title>Select Segment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="automation-settings-modal-search">
                    <MdOutlineSearch size={16} />
                    <input
                        type="text"
                        placeholder="Search by name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="automation-settings-segment-list">
                    {fetching ? (
                        <div className="automation-settings-select-empty">Loading segments...</div>
                    ) : filteredList.length === 0 ? (
                        <div className="automation-settings-select-empty">No segments found.</div>
                    ) : (
                        filteredList.map((s) => (
                            <div className="automation-settings-segment-row" key={s.id}>
                                <span className="automation-settings-segment-row-icon"><MdOutlinePieChart size={16} /></span>
                                <span className="automation-settings-segment-row-name">{s.name}</span>
                                <button
                                    type="button"
                                    className="automation-settings-segment-row-add"
                                    onClick={() => onSelect(s)}
                                    title="Add segment"
                                >
                                    <MdOutlineAddCircleOutline size={18} />
                                </button>
                            </div>
                        ))
                    )}
                </div>
                {manageLink && (
                    <div className="automation-settings-modal-footnote">
                        <Link to={manageLink} target="_blank" rel="noopener noreferrer">Manage segments here.</Link>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default SegmentPickerModal;
